import React from 'react';
import { Zap, Globe, Lock, Briefcase, BarChart3, Clock, ArrowRight } from 'lucide-react';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const features: Feature[] = [
  {
    icon: <Zap size={22} />,
    title: 'Onboard in Minutes',
    description: 'Streamlined KYC and onboarding designed for busy finance teams. Get verified and start paying vendors fast.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-100',
  },
  {
    icon: <Globe size={22} />,
    title: 'Global Vendor Network',
    description: 'Connect with vendors across 50+ countries with real-time exchange rates and transparent fee structures.',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
  },
  {
    icon: <Lock size={22} />,
    title: 'Bank-Grade Security',
    description: 'End-to-end encryption, multi-factor authentication, and compliance-ready audit trails for every transaction.',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-100',
  },
  {
    icon: <Briefcase size={22} />,
    title: 'Vendor Management',
    description: 'Centralized vendor profiles, payment history, and document management in one unified workspace.',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-100',
  },
  {
    icon: <BarChart3 size={22} />,
    title: 'Financial Insights',
    description: 'Real-time dashboards with spend analytics, approval workflows, and exportable reports for your finance team.',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-100',
  },
  {
    icon: <Clock size={22} />,
    title: 'Fast Settlement',
    description: 'Accelerated payment processing with clear status tracking from submission to final settlement.',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-100',
  },
];

const FeaturesSection: React.FC = () => {
  return (
    <section className="py-20 lg:py-28 bg-transparent" id="features">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center mb-14 public-readable-panel">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-fintech-secondary text-xs font-semibold uppercase tracking-wider mb-4">
            Platform Features
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-fintech-text mb-4 tracking-tight">
            Everything You Need to Procure at Scale
          </h2>
          <p className="text-fintech-textMuted text-lg max-w-2xl mx-auto leading-relaxed">
            A complete procurement platform built for finance teams managing global vendor relationships.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative p-6 rounded-2xl border border-fintech-border bg-white/88 backdrop-blur-md hover:border-fintech-secondary/40 hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Subtle background accent on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-blue-50/0 group-hover:from-blue-50/30 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

              {/* Icon */}
              <div className={`relative w-12 h-12 rounded-xl ${feature.bgColor} border ${feature.borderColor} ${feature.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>

              {/* Content */}
              <h3 className="relative text-base font-semibold text-fintech-text mb-2">
                {feature.title}
              </h3>
              <p className="relative text-sm text-fintech-textMuted leading-relaxed">
                {feature.description}
              </p>

              {/* Arrow indicator on hover */}
              <div className="relative mt-4 flex items-center gap-1 text-xs font-medium text-fintech-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span>Learn more</span>
                <ArrowRight size={12} />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom trust strip */}
        <div className="mt-10 lg:mt-14 public-readable-panel public-readable-panel--wide">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-6">
            <p className="text-fintech-textMuted text-sm text-center sm:text-left">
              Trusted by procurement teams at leading enterprises across Africa and beyond.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {[
                { value: '$60M+', label: 'All time processed' },
                { value: '500+', label: 'Businesses' },
                { value: '50+', label: 'Countries' },
              ].map((stat, i) => (
                <div key={i} className="text-center min-w-[30%] sm:min-w-0">
                  <p className="text-base sm:text-lg font-bold text-fintech-primary">{stat.value}</p>
                  <p className="text-[10px] sm:text-xs text-fintech-textMuted uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
